import React from "react";
import { isChunkLoadError } from "@/chunkReloadGuard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Throttle so the same client doesn't spam our endpoint if React keeps retrying.
const SESSION_KEY = "__ftcrash_last_report__";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info);

    // ChunkLoadError = user has stale HTML referencing a JS/CSS chunk that
    // no longer exists after a deploy. Don't log it as a "frontend crash" —
    // just trigger a one-shot reload via the guard so they recover silently.
    if (isChunkLoadError(error)) {
      try {
        const flag = "__kl_chunk_reload_attempted__";
        if (!sessionStorage.getItem(flag)) {
          sessionStorage.setItem(flag, Date.now().toString());
          const u = new URL(window.location.href);
          u.searchParams.set("_klr", Date.now().toString().slice(-6));
          window.location.replace(u.toString());
        }
      } catch (_) { /* swallow */ }
      return;
    }

    // Throttle: skip if we reported the same message within 30s in this tab.
    try {
      const last = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
      const sameMsg = last && last.message === (error?.message || "");
      const fresh = last && Date.now() - last.ts < 30_000;
      if (sameMsg && fresh) return;
    } catch (_) { /* ignore */ }

    // Fire-and-forget POST. Uses sendBeacon when available so the report
    // survives even if React unmounts the page mid-error.
    try {
      const payload = {
        message: String(error?.message || "Unknown error").slice(0, 2000),
        stack: String(error?.stack || "").slice(0, 6000),
        component_stack: String(info?.componentStack || "").slice(0, 3000),
        url: typeof window !== "undefined" ? window.location.href.slice(0, 500) : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : "",
        client_email: (() => {
          try {
            // Best-effort: surface logged-in client email if stored in localStorage.
            const raw = localStorage.getItem("portal_session");
            if (!raw) return null;
            return JSON.parse(raw)?.email || null;
          } catch { return null; }
        })(),
        language: typeof navigator !== "undefined" ? navigator.language : null,
      };
      const url = `${BACKEND_URL}/api/admin/frontend-errors`;
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      let sent = false;
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        sent = navigator.sendBeacon(url, blob);
      }
      if (!sent) {
        // Fallback to fetch with keepalive so it survives page transitions.
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: "omit",
        }).catch(() => { /* swallow — we already crashed once, don't crash again */ });
      }
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ message: payload.message, ts: Date.now() })
      );
    } catch (_) { /* never throw inside the boundary itself */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "40px", textAlign: "center", fontFamily: "sans-serif" }}>
          <h2 style={{ color: "#c9a86c" }}>Something went wrong</h2>
          <p style={{ color: "#888", marginTop: "12px" }}>
            Please try refreshing the page. If the issue persists, contact us.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "20px",
              padding: "12px 24px",
              background: "#c9a86c",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            data-testid="error-boundary-refresh"
          >
            Refresh Page
          </button>
          <p style={{ color: "#555", marginTop: "20px", fontSize: "12px" }}>
            Error: {this.state.error?.message || "Unknown error"}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
